import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextInput, Button, Switch, Label, CustomSelect } from "@/components";
import styles from "../style.module.scss";
import { useAlert } from "@/context/AlertContext";
import {
  checkForCbreEmail,
  checkForExternalEmail,
  userV2SchemaSimplified,
} from "@/lib/validations/userV2";
import { apiV2 } from "@/lib/apiV2/apiV2";
import {
  UserV2Payload,
  UserV2,
  action,
  APPLICATION_NAMES,
  APPLICATION_ID,
  RoleV2Id,
  RoleV2Code,
} from "@/types";
import { useRolesV2 } from "@/hooks/useRolesV2";
import {
  Autocomplete,
  Box,
  CircularProgress,
  styled,
  TextField,
} from "@mui/material";
import { RegionChipSelect } from "@/components/RegionChipSelect/RegionChipSelect";
import { isAbleToCreateUser } from "@/lib/utils/userV2ContextUtils";
import { useUserMeV2Info } from "@/hooks/useUserMeV2Info";
import { sendProviderName } from "@/lib/utils/getProviderTags";
import { TAG_TYPE_NAME, TagTypeTagV2 } from "@/types/tagsV2";
import {
  FAST_MK2_ROLES,
  PLATFORM_AGILE_ROLES,
} from "@/types/userV2Permissions";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useTagsV2 } from "@/hooks/useTagsV2";
import { useApi } from "@/lib/api";
import { MarketsAutocomplete } from "@/components/MarketsAutocomplete";
import { useMutation } from "@tanstack/react-query";

export interface UserFormV2Props {
  onSubmit: (data: UserV2, operation: action) => void;
  selectedUser?: UserV2 | null;
  setModified: (checked: boolean) => void;
}

export interface UserV2FormValues {
  active: boolean;
  name: string;
  email: string;
  roles: {
    roleId: number;
    appId: number;
    tags: number[];
    marketTags: number[];
    regionTags: number[];
  }[];
  tags: number[];
  providerName?: string;
}

interface Roles {
  id: number;
  tags: number[];
}

const mapFormValuesToPayload = (
  values: UserV2FormValues,
  providerTagId: number | null,
  providerOptions: TagTypeTagV2[],
): UserV2Payload => {
  const assignedRoles: Roles[] = [];
  const assignedApplications: number[] = [APPLICATION_ID.FAST_MK2];
  const roleId = values.roles[0].roleId;
  const tags = values.roles[0].tags || [];
  const marketTags = values.roles[0].marketTags || [];
  const regionTags = values.roles[0].regionTags || [];

  if (roleId === RoleV2Id.FAST_MK2_ADMIN) {
    const allTags = [...tags, ...regionTags, ...marketTags];

    assignedRoles.push(
      { id: roleId, tags: allTags },
      { id: RoleV2Id.FAST_MK2_RBAC_ADMIN, tags: allTags },
    );
    assignedApplications.push(APPLICATION_ID.PLATFORM);
  } else if (roleId === RoleV2Id.FAST_MK2_REGIONAL_LEAD) {
    const allTags = [...tags, ...regionTags, ...marketTags];

    assignedRoles.push(
      { id: roleId, tags: allTags },
      { id: RoleV2Id.FAST_MK2_RBAC_REGIONAL_LEAD, tags: allTags },
    );
    assignedApplications.push(APPLICATION_ID.PLATFORM);
  } else if (roleId === RoleV2Id.FAST_MK2_AFS) {
    const allTags = [...tags, ...regionTags, ...marketTags];

    assignedRoles.push(
      { id: roleId, tags: allTags },
      { id: RoleV2Id.FAST_MK2_RBAC_AFS, tags: allTags },
    );
    assignedApplications.push(APPLICATION_ID.PLATFORM);
  } else if (roleId === RoleV2Id.FAST_MK2_PROVIDER_INVENTORY_MANAGER) {
    const providerIds = providerOptions.map((provider) => provider.id);

    const tagsExceptProvider = tags.filter((tag) => !providerIds.includes(tag));

    const providerTags = [
      ...tagsExceptProvider,
      ...marketTags,
      ...regionTags,
      providerTagId!,
    ];

    assignedRoles.push({
      id: roleId,
      tags: providerTags,
    });
  } else {
    const allTags = [...tags, ...regionTags, ...marketTags];

    assignedRoles.push({ id: roleId, tags: allTags });
  }

  return {
    active: values.active,
    applications: assignedApplications,
    clients: [],
    email: values.email.toLocaleLowerCase(),
    name: values.name,
    roles: assignedRoles,
    tags: [],
  };
};

export function UserFormV2Simplified({
  onSubmit,
  selectedUser,
  setModified,
}: UserFormV2Props) {
  const [isFormLoading, setIsFormLoading] = useState<boolean>(false);

  const { tags: providerOptions, loadingTags } = useTagsV2(
    TAG_TYPE_NAME.PROVIDER,
  );

  const alert = useAlert();
  const { roles, loadingRoles } = useRolesV2();
  const {
    isPlatformSuperAdmin,
    isAgileAdmin,
    currentUserAgileRegions,
    isRegionalLead,
    isAdvancedFlexpertSupport,
    user,
  } = useUserMeV2Info();

  const defaultRoleIdForNewUser = () => {
    if (isRegionalLead) return RoleV2Id.FAST_MK2_FLEXPERT;

    if (isAdvancedFlexpertSupport) return RoleV2Id.FAST_MK2_FLEXPERT_SUPPORT;

    return 0;
  };

  const defaultValues = useMemo<UserV2FormValues>(
    () =>
      selectedUser
        ? {
            active: selectedUser.active,
            name: selectedUser.name,
            email: selectedUser.email,
            roles:
              selectedUser.role_associations.length > 0
                ? [
                    {
                      roleId: selectedUser.role_associations[0].id,
                      appId: selectedUser.role_associations[0].application_id,
                      tags: selectedUser.role_associations[0].tags
                        .filter(
                          (tag) =>
                            tag.tag_type !== TAG_TYPE_NAME.REGION &&
                            tag.tag_type !== TAG_TYPE_NAME.MARKET,
                        )
                        .map((tag) => tag.id),
                      regionTags: selectedUser.role_associations[0].tags
                        .filter((tag) => tag.tag_type === TAG_TYPE_NAME.REGION)
                        .map((tag) => tag.id),
                      marketTags: selectedUser.role_associations[0].tags
                        .filter((tag) => tag.tag_type === TAG_TYPE_NAME.MARKET)
                        .map((tag) => tag.id),
                    },
                  ]
                : [
                    {
                      roleId: 0,
                      appId: APPLICATION_ID.FAST_MK2,
                      tags: [],
                      regionTags: [],
                      marketTags: [],
                    },
                  ],
            tags: [],
          }
        : {
            active: true,
            name: "",
            email: "",
            roles: [
              {
                roleId: defaultRoleIdForNewUser(),
                appId: APPLICATION_ID.FAST_MK2,
                tags: [],
                regionTags: [],
                marketTags: [],
              },
            ],
            tags: [],
          },
    [selectedUser],
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    control,
    setValue,
  } = useForm<UserV2FormValues>({
    resolver: yupResolver(userV2SchemaSimplified) as Resolver<UserV2FormValues>,
    defaultValues,
  });

  const currentRoleId = +watch("roles.0.roleId");

  const showActivateButton = () => {
    if (!roles || roles.length === 0) return false;

    const regionalLeadRoleId = roles.find(
      (role) => role.code === RoleV2Code.FAST_MK2_REGIONAL_LEAD,
    )?.id;

    const advancedFlexpertSupportRoleId = roles.find(
      (role) => role.code === RoleV2Code.FAST_MK2_AFS,
    )?.id;

    return !(
      currentRoleId === regionalLeadRoleId ||
      currentRoleId === advancedFlexpertSupportRoleId
    );
  };

  const isProviderInventoryManager =
    Number(currentRoleId) === RoleV2Id.FAST_MK2_PROVIDER_INVENTORY_MANAGER;

  const emailField = watch("email");

  const formData = {
    name: "Created from V2",
    email: emailField,
    role_id: 9999,
    clients_ids: [],
    regions_ids: [],
    is_active: true,
  };

  const api = useApi();

  const userMutation = useMutation({
    mutationFn: async (payload: UserV2Payload) => {
      if (selectedUser) {
        const { data } = await apiV2.users.patch(selectedUser.id, payload);
        return { data, operation: "edit" as action };
      } else {
        const { data } = await apiV2.users.post(payload);
        try {
          api.post("/users", { ...formData, email: data.email });
        } catch (e) {
          console.log("Error when adding to v1 without roleId:", e);
        }
        return { data, operation: "create" as action };
      }
    },
    onSuccess: ({ data, operation }) => {
      onSubmit(data, operation);
      setIsFormLoading(false);
    },
    onError: (e: any) => {
      setIsFormLoading(false);
      console.error("Error", JSON.stringify(e));
      alert("error", e, e?.response?.data?.status);
    },
  });

  const onSubmitInternal = useCallback(
    async (values: UserV2FormValues) => {
      if (!isAbleToCreateUser(user)) {
        alert(
          "error",
          "You are not allowed to create user due to platform policy",
        );
        return;
      }

      setIsFormLoading(true);
      try {
        let providerTagId: number | null = null;

        if (isProviderInventoryManager && values.providerName) {
          const existingProvider = providerOptions.find(
            (provider) =>
              provider.name.toLowerCase() ===
              values.providerName?.toLowerCase(),
          );

          if (existingProvider) {
            providerTagId = existingProvider.id;
          } else {
            const { data: providers } = await apiV2.tags.getTagByTagType(
              TAG_TYPE_NAME.PROVIDER,
            );

            const payload = {
              child_tags: [],
              parent_tags: [],
              name: values.providerName,
              tag_type_id: providers.id,
            };

            const providerResponse = await sendProviderName(payload);
            providerTagId = providerResponse.id;
          }
        }

        const userPayload = mapFormValuesToPayload(
          values,
          providerTagId,
          providerOptions,
        );

        userMutation.mutate(userPayload);
      } catch (e: any) {
        setIsFormLoading(false);
        console.error("Error", JSON.stringify(e));
        alert("error", e, e?.response?.data?.status);
      }
    },
    [
      alert,
      onSubmit,
      selectedUser,
      isProviderInventoryManager,
      providerOptions,
      user,
      userMutation,
    ],
  );

  const activeInputProps = useMemo(() => register("active"), [register]);

  const handleActiveChange = useCallback(
    (checked: boolean) =>
      activeInputProps.onChange({
        target: { name: activeInputProps.name, value: checked },
      }),
    [activeInputProps],
  );

  useEffect(() => {
    setModified(isDirty);
  }, [setModified, isDirty]);

  const roleFieldProps = register("roles.0.roleId", {
    required: true,
  });

  const regionFieldProps = register("roles.0.regionTags", {
    required: true,
  });

  const regions = watch("roles.0.regionTags");

  const marketFieldProps = register("roles.0.marketTags", {
    required: true,
  });

  const handleRegionsChange = (selectedRegions: number[]) => {
    setValue(regionFieldProps.name, selectedRegions);
  };

  const handleMarketsChange = (selectedMarkets: number[]) => {
    setValue(marketFieldProps.name, selectedMarkets);
  };

  const currentMarketTags = watch("roles.0.marketTags");

  const isCbreEmail = checkForCbreEmail.test(emailField);
  const isExternalEmail = checkForExternalEmail.test(emailField);

  const isEmailFieldValid = isCbreEmail || isExternalEmail;

  const isRoleSelectDisabled =
    !isEmailFieldValid || (!isAgileAdmin && !isPlatformSuperAdmin);

  const handleRolesChange = (value: string | number) => {
    roleFieldProps.onChange({
      target: { name: roleFieldProps.name, value: Number(value) },
    });
  };

  const roleOptions = () => {
    const notAllowedAgileAdminRoles = [
      FAST_MK2_ROLES.SUPER_ADMIN,
      FAST_MK2_ROLES.ADMIN,
      FAST_MK2_ROLES.PROVIDER_INVENTORY_MANAGER,
      FAST_MK2_ROLES.CLIENT,
    ];
    const externalEmailRolesAllowed = [
      FAST_MK2_ROLES.PROVIDER_INVENTORY_MANAGER,
      FAST_MK2_ROLES.CLIENT,
    ];

    if (isRegionalLead) {
      return [
        {
          label: "Flexpert",
          value: "10",
        },
      ];
    }

    if (isAdvancedFlexpertSupport) {
      return [
        {
          label: "Flexpert Support",
          value: "11",
        },
      ];
    }

    return roles
      .filter((role) =>
        role.application_id === APPLICATION_ID.FAST_MK2 &&
        isAgileAdmin &&
        isCbreEmail
          ? !notAllowedAgileAdminRoles.includes(role.code as FAST_MK2_ROLES)
          : isExternalEmail &&
            externalEmailRolesAllowed.includes(role.code as FAST_MK2_ROLES),
      )
      .map((role) => ({
        label: role.name,
        value: role.id.toString(),
      }));
  };

  const regionOptions = currentUserAgileRegions
    ? currentUserAgileRegions.map((region) => ({
        label: region.label,
        value: region.value,
      }))
    : [];

  const regionsToFetchMarkets = regions.filter((region) =>
    regionOptions.map((region) => region.value).includes(region),
  );

  const marketsPerRegion = useQueries({
    queries: regionsToFetchMarkets.map((region) => ({
      queryKey: ["marketsPerRegion", region],
      queryFn: () => apiV2.tags.getByTagId(region),
      enabled: !!region,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    })),
  });

  const markets = marketsPerRegion.flatMap((data) => data.data?.data) || [];

  const getDefaultRoleValue = () => {
    if (selectedUser) {
      const agileApp = selectedUser?.role_associations.filter(
        (role) => role.application === APPLICATION_NAMES.FAST_MK2,
      );
      return agileApp?.[0]?.id.toString();
    } else {
      if (isRegionalLead) {
        return RoleV2Id.FAST_MK2_FLEXPERT.toString();
      }

      if (isAdvancedFlexpertSupport) {
        return RoleV2Id.FAST_MK2_FLEXPERT_SUPPORT.toString();
      }
    }
  };

  const getDefaultProviderValue = () => {
    if (!(selectedUser && isProviderInventoryManager)) return "";

    const providerInventoryRole = selectedUser.role_associations.find(
      (role) => role.code === RoleV2Code.FAST_MK2_PROVIDER_INVENTORY_MANAGER,
    );

    const providerName = providerInventoryRole?.tags.filter(
      (tag) => tag.tag_type === TAG_TYPE_NAME.PROVIDER,
    )[0]?.name;

    return providerName || "";
  };

  const isMarketsLoading =
    regions.length > 0 && marketsPerRegion.some((market) => market.isLoading);

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmitInternal)}>
      <header>
        <h2 className={styles.title}>
          {selectedUser ? "Edit User" : "New User"}
        </h2>
      </header>
      <div className={`${styles.section} ${styles.content}`}>
        <TextInput
          label="Full Name*"
          {...register("name", { required: true })}
          error={!!errors.name}
          helperText={errors.name?.message}
          placeholder="Add Full Name"
        />
        <TextInput
          label="Email"
          required
          {...register("email", { required: true })}
          error={!!errors.email}
          helperText={errors.email?.message}
          disabled={!!selectedUser}
          className={!!selectedUser ? styles.disabledEmailField : ""}
          placeholder="Add Email"
        />
      </div>

      <div className={styles.section}>
        <CustomSelect
          label="Select Role*"
          options={roleOptions()}
          {...roleFieldProps}
          onChange={handleRolesChange}
          defaultValue={getDefaultRoleValue()}
          placeholder="Select User Role"
          disabled={isRoleSelectDisabled || loadingRoles}
          error={!!errors.roles?.[0]?.roleId}
          helperText={errors.roles?.[0]?.roleId?.message}
          loading={loadingRoles}
        />
        {isProviderInventoryManager && (
          <Box sx={{ marginTop: "24px" }}>
            <Label label="Provider*">
              <Controller
                name="providerName"
                defaultValue={getDefaultProviderValue()}
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={providerOptions.map((provider) => provider.name)}
                    renderInput={(params) => (
                      <TextField
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                {loadingTags && <CircularProgress size={20} />}
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          },
                        }}
                        error={!!errors.providerName}
                        {...params}
                      />
                    )}
                    value={field.value}
                    onInputChange={(event, newValue) =>
                      field.onChange(newValue)
                    }
                    freeSolo
                    clearOnBlur={false}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "40px",

                        "& .MuiInputBase-input": {
                          fontSize: "20px",
                        },
                      },
                    }}
                  />
                )}
              />
            </Label>
            {!!errors.providerName && (
              <p style={{ fontSize: "13px", letterSpacing: ".5px" }}>
                {errors.providerName.message}
              </p>
            )}
          </Box>
        )}
      </div>
      <div className={styles.section}>
        <RegionChipSelect
          regions={regionOptions}
          onChange={handleRegionsChange}
          value={regions}
          error={!!errors.roles?.[0]?.regionTags}
          helperText={errors.roles?.[0]?.regionTags?.message}
        />
        <Box sx={{ marginTop: "24px" }}>
          <MarketsAutocomplete
            data={markets}
            handleMarketsChange={handleMarketsChange}
            currentMarketTags={currentMarketTags}
            error={!!errors.roles?.[0]?.marketTags}
            helperText={errors.roles?.[0]?.marketTags?.message}
            disabled={regions.length === 0}
            loading={isMarketsLoading}
          />
        </Box>
      </div>
      <footer className={styles.footer}>
        <div className={styles.section}>
          {showActivateButton() && (
            <label className={styles.switch} htmlFor="active">
              <Switch
                id="active"
                defaultChecked={defaultValues.active}
                onCheckedChange={handleActiveChange}
              />
              <h6 className={styles.subtitle}>Activate User</h6>
            </label>
          )}
        </div>
        <Button type="submit">
          {isFormLoading ? <CircularProgress size="30px" /> : "Save Changes"}
        </Button>
      </footer>
    </form>
  );
}
